import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { workspaceSlice, selectSelectedTab } from 'store/workspace/slice';
import { T_editWorkspaceFilterOptions } from 'store/workspace/thunk';
import { T_getBenchmarkData } from 'store/explore/thunk';
import { trackGAEvent } from 'helpers/functions/GoogleAnalytics/GA';
import { eventsGA } from 'helpers/functions/GoogleAnalytics/types';
import { IElementType } from 'components/SideBarItems/types';

interface IFilterOptionChange {
    element?: IElementType;
    value?: unknown;
    group?: string | null;
    index?: number;
}

// Shared by the filter toolbar and the legend panel's scope notices. Benchmark data is persisted
// here rather than left to SideBar's filterOptions effect, because the refetch below has to see
// the new value server-side - that ordering is the reason this is not just a dispatch.
export const useFilterOptionToggle = () => {
    const dispatch = useAppDispatch();
    const { workspaceId, workspaces, filters: { filterOptions } } = useAppSelector(state => state.workspaceSlice);
    const selectedTab = useAppSelector(selectSelectedTab);

    return useCallback(async (obj: IFilterOptionChange): Promise<void> => {
        // Filter-level toggles (isEnabled / applyToUserData / applyToBenchmarkData) send
        // their request on read-only workspaces too; the backend patch decides allow/deny.
        dispatch(workspaceSlice.actions.setFilterOptionItem(obj));

        switch (obj.element) {
            case 'applyToUserData':
                trackGAEvent(eventsGA.filters_applied_user_data);
                break;
            case 'applyToBenchmarkData':
                trackGAEvent(eventsGA.filters_applied_benchmark);
                if (selectedTab?.type === 7 && selectedTab?.id && workspaceId) {
                    const foundWorkspace = workspaces.find(item => item.id === workspaceId);
                    if (foundWorkspace) {
                        try {
                            await dispatch(T_editWorkspaceFilterOptions({
                                id: workspaceId,
                                filterOptions: {
                                    ...filterOptions,
                                    applyToBenchmarkData: obj.value as boolean
                                },
                            }));

                            dispatch(T_getBenchmarkData({ workspaceId, id: selectedTab.id }));
                        } catch (error) {
                            console.error('Error updating workspace filters or benchmark data:', error);
                        }
                    }
                }
                break;
            default:
                break;
        }
    }, [dispatch, workspaceId, workspaces, filterOptions, selectedTab]);
};
